// State
import { exportableStore as store } from '../state/store';
import { setError } from '../state/modules/errors/actions';

// Utils
import { getAccessToken, removeAccessToken, removeUnauthorizedToken } from './auth';
import isomorphicCookies from './isomorphicCookies';

// Config
import config from '../config';

function getQueryString(params) {
  const esc = encodeURIComponent;

  return Object.keys(params)
    .map(k => `${esc(k)}=${esc(params[k])}`)
    .join('&');
}

function request(params) {
  const baseUrl = config.apiUrl;
  const method = params.method || 'GET';
  const isFormData = params.type === 'form-data';
  const defaultHeaders = { Accept: 'application/json' };
  const userHeaders = params.headers || {};
  const headers = {
    ...defaultHeaders,
    ...userHeaders,
  };
  const ssr = params.ssr ?? false;
  const requestTimeoutMs =
    params.timeoutMs ?? (ssr ? Number(process.env.SSR_UPSTREAM_TIMEOUT_MS || 8000) : 0);

  let qs = '';
  let body;

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const { isRealPhone, isRealTablet } = store.getState().responsive;
  const platform = isRealPhone || isRealTablet ? 'web-mobile' : 'web';
  const lang = isomorphicCookies().get('locale') || config.lang;

  // у неавторизованного пользователя нет access_token
  const access_token = getAccessToken();
  const tokens = access_token ? { access_token } : {};

  // а вот city_id может быть undefined, тогда мы его не записываем - некоторые запросы сломаются
  const city_id = isomorphicCookies().get('city_id');
  const cities = city_id ? { city_id } : {};

  // накдиываем сверху данных из вызовов, которые чисто теоретически могут перезаписать lang, platform, city_id
  // это может пригодиться
  const data = {
    lang,
    platform,
    ...cities,
    ...tokens,
    ...params.data,
  };

  // убираем подсказку в правом нижнем углу
  const withError = data.withError === undefined ? true : data.withError;

  if (isFormData) {
    // eslint-disable-next-line no-undef
    const form_data = new FormData();

    Object.keys(data).forEach(key => {
      if (Array.isArray(data[key])) {
        data[key].forEach(value => {
          form_data.append(`${key}[]`, value);
        });
      } else {
        form_data.append(key, data[key]);
      }
    });

    body = form_data;
  } else if (['GET', 'DELETE'].indexOf(method) > -1) {
    // GET
    qs = `?${getQueryString(data)}`;

    if (params.body) {
      body = JSON.stringify(params.body);
    }
  } else {
    // POST or PUT
    body = JSON.stringify(data);
  }

  const url = (ssr ? `${config.ssrUrl}` : baseUrl) + params.url + qs;

  const fetchPromise = fetch(url, {
    method,
    headers,
    body,
    credentials: config.credentials || 'same-origin',
  }).then(async response => {
    let text = null;
    let json = {};

    try {
      text = await response.text();
      json = text ? JSON.parse(text) : {};
    } catch (error) {
      json = {};
    }

    if (response.ok) {
      return json;
    }

    const message = json.message || text;

    if (response.status === 401) {
      removeAccessToken();
      removeUnauthorizedToken();
    } else if (message) {
      if (typeof message === 'object') {
        if (withError) {
          Object.keys(message).map(key => store.dispatch(setError(message[key])));
        }
      } else if (
        typeof message === 'string' &&
        message.toLowerCase().indexOf('промо-код') === -1 &&
        message.toLowerCase().indexOf('promo') === -1
      ) {
        if (withError) {
          store.dispatch(setError(message));
        }
      }
    }

    return Promise.reject({
      ...json,
      status: response.status,
      statusText: response.statusText,
      raw: text,
    });
  });

  if (!requestTimeoutMs) {
    return fetchPromise;
  }

  return Promise.race([
    fetchPromise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Request timeout after ${requestTimeoutMs}ms`)), requestTimeoutMs);
    }),
  ]);
}

export const get = params => request({ method: 'GET', ...params });
export const post = params => request({ method: 'POST', ...params });
export const put = params => request({ method: 'PUT', ...params });
export const del = params => request({ method: 'DELETE', ...params });
export const patch = params => request({ method: 'PATCH', ...params });
